import { supabase } from './supabaseService';
import {
  encodeBullhornQueryFields,
  JOB_SUBMISSION_FIELDS_BY_CANDIDATE,
  JOB_SUBMISSION_FIELDS_BY_CANDIDATE_NO_HISTORY,
  JOB_SUBMISSION_FIELDS_BY_JOB,
  JOB_SUBMISSION_FIELDS_BY_JOB_NO_HISTORY,
} from './bullhornJobSubmissionHistory';

export type { JobSubmissionStatusEvent } from './bullhornJobSubmissionHistory';
export { normalizeJobSubmissionHistory, normalizeBullhornTimestamp } from './bullhornJobSubmissionHistory';

export interface BullhornAuthData {
  BhRestToken: string;
  restUrl: string;
  logs?: string[];
}

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

/** Base URL for `/api/bullhorn/*` — browser uses same origin (Vite proxy); Node uses localhost:4000. */
export function getBullhornProxyBaseUrl(): string {
  let u: string | undefined;
  if (typeof import.meta !== 'undefined') {
    u = (import.meta as ImportMeta & { env?: { VITE_SERVER_URL?: string } }).env?.VITE_SERVER_URL;
  }
  if (!u && typeof process !== 'undefined') {
    u = (process as NodeJS.Process & { env?: { VITE_SERVER_URL?: string } }).env?.VITE_SERVER_URL;
  }
  if (u && !u.includes('apiUrl')) return u.replace(/\/$/, '');
  if (typeof window !== 'undefined') return '';
  return 'http://localhost:4000';
}

const BULLHORN_BACKEND_HINT =
  'Start de API-server: npm run server (poort 4000) of npm run dev:all (frontend + server).';

/** Paginated query/JobSubmission; throws on HTTP errors (used with history fallback). */
async function fetchAllJobSubmissionsPaged(
  auth: BullhornAuthData,
  whereClause: string,
  fields: string,
  pageSize: number,
  orderBy: string = 'id'
): Promise<any[]> {
  const apiUrl = getBullhornProxyBaseUrl();
  const encodedFields = encodeBullhornQueryFields(fields);
  let allData: any[] = [];
  let start = 0;

  while (true) {
    const endpoint = `query/JobSubmission?where=${encodeURIComponent(whereClause)}&fields=${encodedFields}&count=${pageSize}&start=${start}&orderBy=${orderBy}`;
    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        BhRestToken: auth.BhRestToken,
        restUrl: auth.restUrl,
        endpoint,
        method: 'GET',
      }),
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      const err = new Error(`JobSubmissions API error ${res.status}: ${errText}`) as Error & { status?: number };
      err.status = res.status;
      throw err;
    }

    const json = await res.json();
    if (json.errorMessage) throw new Error(`Bullhorn: ${json.errorMessage}`);
    if (!json.data || json.data.length === 0) break;

    allData = allData.concat(json.data);
    if (json.data.length < pageSize) break;
    start += pageSize;
  }

  return allData;
}

/** Request JobSubmission including `history`; retry without `history` if the stack rejects the field. */
async function fetchAllJobSubmissionsWithHistoryFallback(
  auth: BullhornAuthData,
  whereClause: string,
  fieldsWithHistory: string,
  fieldsNoHistory: string,
  pageSize: number,
  orderBy?: string
): Promise<any[]> {
  try {
    return await fetchAllJobSubmissionsPaged(auth, whereClause, fieldsWithHistory, pageSize, orderBy);
  } catch (e: any) {
    if (e?.status === 401 || e?.status === 403) throw e;
    const combined = `${e?.message ?? e}`;
    if (/history|JobSubmissionHistory/i.test(combined)) {
      return fetchAllJobSubmissionsPaged(auth, whereClause, fieldsNoHistory, pageSize, orderBy);
    }
    throw e;
  }
}

/**
 * Service to handle Bullhorn REST API authentication and requests.
 * Due to CORS limitations when calling from the browser, we ideally
 * use a Supabase Edge Function. For this demo, we will try direct calls,
 * but expect them to fail in browser without a proxy.
 */

export const testBullhornConnection = async (credentials: {
  clientId: string;
  clientSecret: string;
  apiUsername: string;
  apiPassword: string;
}): Promise<BullhornAuthData> => {
  try {
    // Call the local Express backend proxy which safely completes the OAuth cycle
    // (Bypasses browser CORS constraints and redirect limitations)
    
    // In dev it's usually localhost:4000. In prod, assuming same domain or env var.
    const apiUrl = getBullhornProxyBaseUrl();
    
    const res = await fetch(`${apiUrl}/api/bullhorn/auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(credentials)
    });

    if (!res.ok) {
        let errData;
        const errText = await res.text();
        try {
            errData = JSON.parse(errText);
        } catch(e) {
            errData = { error: errText };
        }
        
        // If the backend provided partial logs before failing, we can throw those too.
        if (errData.logs) {
             throw new Error(JSON.stringify({ message: errData.error || errData.details, logs: errData.logs }));
        }

        throw new Error(errData.error || errData.details || `Failed to connect proxy: ${res.status}`);
    }

    const data = await res.json();
    return {
        BhRestToken: data.BhRestToken,
        restUrl: data.restUrl,
        logs: data.logs || []
    };

  } catch (error: unknown) {
    console.error('Bullhorn Auth Proxy Error:', error);
    const msg = error instanceof Error ? error.message : String(error);
    if (msg === 'Failed to fetch' || (error instanceof TypeError && /fetch/i.test(msg))) {
      throw new Error(BULLHORN_BACKEND_HINT);
    }
    throw error;
  }
};

export const fetchBullhornCandidatesRaw = async (auth: BullhornAuthData, query: string = "isDeleted:0", fetchAll: boolean = false) => {
    const apiUrl = getBullhornProxyBaseUrl();
    let allData: any[] = [];
    let start = 0;
    const count = 500; // max pagination chunk size
    
    while (true) {
        const endpoint = `search/Candidate?query=${encodeURIComponent(query)}&fields=id,firstName,lastName,email,email2,phone,mobile,address,status,dateAdded,customText1,customText2,customText3&count=${count}&start=${start}&sort=-dateAdded`;
        
        const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                restUrl: auth.restUrl,
                BhRestToken: auth.BhRestToken,
                endpoint,
                method: 'GET'
            })
        });

        if (!res.ok) {
            throw new Error(`Failed to fetch candidates: ${res.status}`);
        }

        const json = await res.json();
        if (json.data && Array.isArray(json.data)) {
            allData = allData.concat(json.data);
            if (!fetchAll || allData.length >= json.total || json.data.length < count) {
                return { data: allData, total: json.total || allData.length };
            }
            start += count;
        } else {
            return { data: allData, total: allData.length };
        }
    }
};

let cachedCandidateFields: string | null = null;

export const fetchBullhornCandidateFull = async (auth: BullhornAuthData, candidateId: string | number) => {
    const apiUrl = getBullhornProxyBaseUrl();
    
    // 1. Dynamically fetch ALL authorized fields for this API User via /meta
    if (!cachedCandidateFields) {
        try {
            const metaRes = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    restUrl: auth.restUrl,
                    BhRestToken: auth.BhRestToken,
                    endpoint: 'meta/Candidate?fields=*&meta=basic',
                    method: 'GET'
                })
            });
            if (metaRes.ok) {
                const metaJson = await metaRes.json();
                if (metaJson.fields && Array.isArray(metaJson.fields)) {
                    cachedCandidateFields = metaJson.fields.map((f: any) => f.name).join(',');
                }
            }
        } catch (e) {
            console.warn("Failed caching Bullhorn meta fields. Falling back to explicit safe-list.", e);
        }
    }

    // Fallback if meta fails
    const defaultSafeList = "id,firstName,lastName,name,email,email2,email3,phone,phone2,phone3,mobile,address,status,category,customText1,customText2,customText3,customText4,customText5,customText6,customText7,customText8,customText9,customText10,customDate1,customDate2,customDate3,dateAdded,dateLastModified,owner,source,occupation,companyName,description,skillSet";
    
    // Prevent 400 Auth error safely by strictly using standard explicitly whitelisted fields
    const fieldsList = `${defaultSafeList},educations(id,school,degree,startDate,endDate),workHistories(id,companyName,title,startDate,endDate,comments)`;

    const endpoint = `entity/Candidate/${candidateId}?fields=${fieldsList}`;
    
    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            restUrl: auth.restUrl,
            BhRestToken: auth.BhRestToken,
            endpoint,
            method: 'GET'
        })
    });

    if (!res.ok) throw new Error(`Failed to fetch full candidate: ${res.status}`);
    const json = await res.json();
    return json.data; // Entity queries return { data: { ... } }
};

export const fetchBullhornJobOrderMeta = async (auth: BullhornAuthData) => {
    const apiUrl = getBullhornProxyBaseUrl();
    const endpoint = `meta/JobOrder?fields=*`;
    
    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            restUrl: auth.restUrl,
            BhRestToken: auth.BhRestToken,
            endpoint,
            method: 'GET'
        })
    });

    if (!res.ok) throw new Error(`Failed to fetch JobOrder Meta: ${res.status}`);
    return await res.json();
};

export const fetchBullhornJobOrdersRaw = async (auth: BullhornAuthData, query: string = "isDeleted:0", isSearch: boolean = true, fetchAll: boolean = false, onProgress?:(msg:string)=>void) => {
    const apiUrl = getBullhornProxyBaseUrl();
    const fields = "id,title,status,dateAdded,dateLastModified,clientCorporation,clientContact,description,salary,salaryUnit,employmentType,durationWeeks,numOpenings,owner,address";
    const endpointPrefix = isSearch ? 'search' : 'query';
    const queryParam = isSearch ? 'query' : 'where';
    
    let allData: any[] = [];
    let start = 0;
    // Gebruik 100 om timeouts te voorkomen (500 is vaak te zwaar voor beschrijvingen)
    const count = fetchAll ? 100 : 100;

    while (true) {
        const endpoint = `${endpointPrefix}/JobOrder?${queryParam}=${encodeURIComponent(query)}&fields=${fields}&count=${count}&start=${start}&sort=-dateAdded`;
        const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                restUrl: auth.restUrl,
                BhRestToken: auth.BhRestToken,
                endpoint,
                method: 'GET'
            })
        });

        if (!res.ok) {
            throw new Error(`Failed to fetch JobOrders: ${res.status}`);
        }
        const json = await res.json();
        if (!json.data || json.data.length === 0) {
            if (!fetchAll) {
                return { data: allData, total: json.total ?? 0 };
            }
            break;
        }
        allData = allData.concat(json.data);
        if (onProgress) onProgress(`Vacatures inladen... (${allData.length} records)`);
        if (!fetchAll) {
            return { data: allData, total: json.total ?? allData.length };
        }
        start += count;
        if (fetchAll) await delay(300); // Throttle deep pagination
    }

    return { data: allData, total: allData.length };
};


export const fetchBullhornCandidateJobSubmissions = async (auth: BullhornAuthData, candidateId: number | string) => {
    const whereClause = `candidate.id=${candidateId}`;
    return fetchAllJobSubmissionsWithHistoryFallback(
        auth,
        whereClause,
        JOB_SUBMISSION_FIELDS_BY_CANDIDATE,
        JOB_SUBMISSION_FIELDS_BY_CANDIDATE_NO_HISTORY,
        100,
        'id'
    );
};

/** Flatten Bullhorn to-many association (array or `{ data: [] }`). */
function flattenBhToMany(raw: unknown): unknown[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'object' && raw !== null && Array.isArray((raw as { data?: unknown[] }).data)) {
    return (raw as { data: unknown[] }).data;
  }
  return [];
}

async function fetchJobSubmissionsViaJobOrderEntity(
  auth: BullhornAuthData,
  jobOrderId: string | number,
  submissionFieldList: string
): Promise<any[]> {
  const apiUrl = getBullhornProxyBaseUrl();
  const fieldsRaw = `id,title,submissions[200](${submissionFieldList})`;
  const fields = encodeBullhornQueryFields(fieldsRaw);
  const endpoint = `entity/JobOrder/${jobOrderId}?fields=${fields}`;
  const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ restUrl: auth.restUrl, BhRestToken: auth.BhRestToken, endpoint, method: 'GET' }),
  });
  if (!res.ok) return [];
  const json = await res.json();
  if (json.errorMessage) return [];
  const subs = json.data?.submissions;
  return flattenBhToMany(subs) as any[];
}

async function fetchJobSubmissionsViaSearch(
  auth: BullhornAuthData,
  submissionFieldList: string,
  luceneQuery: string
): Promise<any[]> {
  const apiUrl = getBullhornProxyBaseUrl();
  const encodedFields = encodeBullhornQueryFields(submissionFieldList);
  let allData: any[] = [];
  let start = 0;
  const pageSize = 200;
  while (true) {
    const endpoint = `search/JobSubmission?query=${encodeURIComponent(luceneQuery)}&fields=${encodedFields}&count=${pageSize}&start=${start}`;
    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ restUrl: auth.restUrl, BhRestToken: auth.BhRestToken, endpoint, method: 'GET' }),
    });
    if (!res.ok) return allData;
    const json = await res.json();
    if (json.errorMessage) return allData;
    if (!json.data || json.data.length === 0) break;
    allData = allData.concat(json.data);
    if (json.data.length < pageSize) break;
    start += pageSize;
  }
  return allData;
}

async function tryJobOrderEntityWithHistoryFallback(
  auth: BullhornAuthData,
  jobOrderId: string | number
): Promise<any[]> {
  let rows = await fetchJobSubmissionsViaJobOrderEntity(auth, jobOrderId, JOB_SUBMISSION_FIELDS_BY_JOB);
  if (rows.length > 0) return rows;
  rows = await fetchJobSubmissionsViaJobOrderEntity(auth, jobOrderId, JOB_SUBMISSION_FIELDS_BY_JOB_NO_HISTORY);
  return rows;
}

async function tryJobOrderSearchWithHistoryFallback(
  auth: BullhornAuthData,
  jobOrderId: string | number
): Promise<any[]> {
  const idStr = String(jobOrderId).trim();
  const queries = [`jobOrder.id:${idStr}`, `jobOrder:${idStr}`];
  for (const q of queries) {
    let rows = await fetchJobSubmissionsViaSearch(auth, JOB_SUBMISSION_FIELDS_BY_JOB, q);
    if (rows.length > 0) return rows;
    rows = await fetchJobSubmissionsViaSearch(auth, JOB_SUBMISSION_FIELDS_BY_JOB_NO_HISTORY, q);
    if (rows.length > 0) return rows;
  }
  return [];
}

export type BullhornJobSubmissionFetchMethod = 'query' | 'entity' | 'search' | null;

/** Same data as fetchBullhornJobSubmissions plus which Bullhorn call returned rows (for diagnostics). */
export const fetchBullhornJobSubmissionsWithMeta = async (
  auth: BullhornAuthData,
  jobOrderId: string | number
): Promise<{ submissions: any[]; method: BullhornJobSubmissionFetchMethod }> => {
  const whereClause = `jobOrder.id=${jobOrderId}`;
  let rows = await fetchAllJobSubmissionsWithHistoryFallback(
    auth,
    whereClause,
    JOB_SUBMISSION_FIELDS_BY_JOB,
    JOB_SUBMISSION_FIELDS_BY_JOB_NO_HISTORY,
    200,
    'id'
  );
  if (rows.length > 0) return { submissions: rows, method: 'query' };

  rows = await tryJobOrderEntityWithHistoryFallback(auth, jobOrderId);
  if (rows.length > 0) return { submissions: rows, method: 'entity' };

  rows = await tryJobOrderSearchWithHistoryFallback(auth, jobOrderId);
  if (rows.length > 0) return { submissions: rows, method: 'search' };

  return { submissions: [], method: null };
};

export const fetchBullhornJobSubmissions = async (auth: BullhornAuthData, jobOrderId: string | number) => {
  const { submissions } = await fetchBullhornJobSubmissionsWithMeta(auth, jobOrderId);
  return submissions;
};

/** Fetch all Notes linked to a JobOrder from Bullhorn via entity association */
export const fetchBullhornJobNotes = async (auth: BullhornAuthData, jobOrderId: string | number) => {
    const apiUrl = getBullhornProxyBaseUrl();

    try {
        // Bullhorn: get notes via JobOrder entity association
        const fields = encodeURIComponent('id,action,comments,dateAdded,personReference(id,firstName,lastName,email)');
        const endpoint = `entity/JobOrder/${jobOrderId}/notes?fields=${fields}&count=200&start=0`;

        const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ restUrl: auth.restUrl, BhRestToken: auth.BhRestToken, endpoint, method: 'GET' })
        });

        if (!res.ok) return []; // Not all JobOrders have notes, fail silently
        const json = await res.json();
        return json.data || [];
    } catch {
        return [];
    }
};



export const fetchAllCandidatesViaQuery = async (auth: BullhornAuthData, whereClause: string = "isDeleted=false", fields: string = "id,firstName,lastName,email,phone,mobile,status,dateAdded,source,employmentPreference,skillSet,description,address(address1,address2,city,zip,state,countryName),workHistories[10](companyName,title,startDate,endDate,comments),educations[5](school,degree,startDate,endDate)", onProgress?:(msg:string)=>void) => {
    const apiUrl = getBullhornProxyBaseUrl();
    
    let allData: any[] = [];
    let start = 0;
    const fetchCount = 200;

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    while (true) {
        const safeFields = fields.replace(/\[/g, "%5B").replace(/\]/g, "%5D").replace(/\(/g, "%28").replace(/\)/g, "%29");
        const endpoint = `query/Candidate?where=${encodeURIComponent(whereClause)}&fields=${safeFields}&count=${fetchCount}&start=${start}&orderBy=-dateAdded`;
        
        let res;
        let retries = 0;
        const maxRetries = 4;
        
        while (retries <= maxRetries) {
            res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    BhRestToken: auth.BhRestToken,
                    restUrl: auth.restUrl,
                    endpoint: endpoint,
                    method: 'GET'
                })
            });

            if (res.status === 429 && retries < maxRetries) {
                const waitMs = Math.pow(2, retries) * 1500;
                if (onProgress) onProgress(`SQL Rate limit (429) bereikt, wachten... (${retries + 1}/${maxRetries})`);
                await delay(waitMs);
                retries++;
            } else {
                break;
            }
        }

        if (!res || !res.ok) {
            const errTxt = await res?.text().catch(()=>'');
            console.error("SQL 400 Error:", errTxt);
            throw new Error(`Bullhorn Query Failed: ${res?.status} - ${errTxt}`);
        }
        
        const json = await res.json();
        if (!json.data || json.data.length === 0) break;
        allData = allData.concat(json.data);
        if (onProgress) onProgress(`Kandidaten inladen SQL... (${allData.length} records)`);
        
        start += fetchCount;
        await delay(300); // Throttle deep pagination
    }

    return { data: allData, total: allData.length };
};

export const importCandidatesFromBullhorn = async (auth: BullhornAuthData, query: string = "isDeleted:0", fields: string = "id,firstName,lastName,email,phone,mobile,status,dateAdded,source,employmentPreference,skillSet,description,workHistories[10](companyName,title,startDate,endDate,comments),educations[5](school,degree,startDate,endDate)", count: number = 100, fetchAll: boolean = false, onProgress?:(msg:string)=>void) => {
    const apiUrl = getBullhornProxyBaseUrl();
    
    let allData: any[] = [];
    let start = 0;
    // Gebruik 200 om timeouts te voorkomen
    const fetchCount = fetchAll ? 200 : count;

    while (true) {
        const endpoint = `search/Candidate?query=${encodeURIComponent(query)}&fields=${fields.replace(/\[/g, "%5B").replace(/\]/g, "%5D").replace(/\(/g, "%28").replace(/\)/g, "%29")}&count=${fetchCount}&start=${start}&sort=-dateAdded`;
        let res;
        let retries = 0;
        const maxRetries = 4;
        
        while (retries <= maxRetries) {
            res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    restUrl: auth.restUrl,
                    BhRestToken: auth.BhRestToken,
                    endpoint,
                    method: 'GET'
                })
            });

            if (res.status === 429 && retries < maxRetries) {
                const waitMs = Math.pow(2, retries) * 1500;
                if (onProgress) onProgress(`Rate limit (429) bereikt, wachten... (${retries + 1}/${maxRetries})`);
                await delay(waitMs);
                retries++;
            } else {
                break;
            }
        }

        if (!res || !res.ok) {
            throw new Error(`Failed to import candidates: ${res?.status}`);
        }
        const json = await res.json();
        if (!json.data || json.data.length === 0) break;
        allData = allData.concat(json.data);
        if (onProgress) onProgress(`Kandidaten inladen... (${allData.length} records)`);
        if (!fetchAll) break;
        start += fetchCount;
        if (fetchAll) await delay(300); // Throttle deep pagination
    }

    return { data: allData, total: allData.length };
};

export const exportCandidateToBullhorn = async (auth: BullhornAuthData, candidateData: any, bullhornId?: string | number) => {
    const apiUrl = getBullhornProxyBaseUrl();
    const method = bullhornId ? 'POST' : 'PUT';
    const endpoint = bullhornId ? `entity/Candidate/${bullhornId}` : 'entity/Candidate';
    
    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            restUrl: auth.restUrl,
            BhRestToken: auth.BhRestToken,
            endpoint,
            method,
            data: candidateData
        })
    });

    if (!res.ok) {
        let errText = await res.text();
        try {
            const parsed = JSON.parse(errText);
            if(parsed.errorMessage) errText = parsed.errorMessage;
        } catch(e){}
        throw new Error(`Failed to export candidate. Status: ${res.status}. ${errText}`);
    }

    const json = await res.json();
    return json.changedEntityId || bullhornId;
};

export const fetchBullhornJobOrderFiles = async (auth: BullhornAuthData, jobOrderId: string | number) => {
    const apiUrl = getBullhornProxyBaseUrl();
    const endpoint = `entity/JobOrder/${jobOrderId}/fileAttachments?fields=id,name,description,dateAdded,contentType`;

    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            restUrl: auth.restUrl,
            BhRestToken: auth.BhRestToken,
            endpoint,
            method: 'GET'
        })
    });

    if (!res.ok) throw new Error(`Failed to fetch JobOrder files: ${res.status}`);
    const json = await res.json();
    return json.data || [];
};

export const exportCandidateWorkHistory = async (auth: BullhornAuthData, bhCandidateId: string | number, workData: any) => {
    const apiUrl = getBullhornProxyBaseUrl();
    const payload = { ...workData, candidate: { id: bhCandidateId } };

    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            restUrl: auth.restUrl,
            BhRestToken: auth.BhRestToken,
            endpoint: 'entity/CandidateWorkHistory',
            method: 'PUT',
            data: payload
        })
    });

    if (!res.ok) throw new Error(`Failed to export Work History: ${res.status}`);
    return await res.json();
};

export const exportCandidateEducation = async (auth: BullhornAuthData, bhCandidateId: string | number, eduData: any) => {
    const apiUrl = getBullhornProxyBaseUrl();
    const payload = { ...eduData, candidate: { id: bhCandidateId } };

    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            restUrl: auth.restUrl,
            BhRestToken: auth.BhRestToken,
            endpoint: 'entity/CandidateEducation',
            method: 'PUT',
            data: payload
        })
    });

    if (!res.ok) throw new Error(`Failed to export Education: ${res.status}`);
    return await res.json();
};

export const exportCandidateNote = async (auth: BullhornAuthData, bhCandidateId: string | number, action: string, comments: string) => {
    const apiUrl = getBullhornProxyBaseUrl();
    const payload = { 
        action: action, 
        comments: comments, 
        personReference: { id: bhCandidateId } 
    };

    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            restUrl: auth.restUrl,
            BhRestToken: auth.BhRestToken,
            endpoint: 'entity/Note',
            method: 'PUT',
            data: payload
        })
    });

    if (!res.ok) throw new Error(`Failed to export Note: ${res.status}`);
    return await res.json();
};

let cachedNoteFields: string | null = null;

export const fetchBullhornCandidateNotes = async (auth: BullhornAuthData, bhCandidateId: string | number) => {
    const apiUrl = getBullhornProxyBaseUrl();
    
    const endpoint = `entity/Candidate/${bhCandidateId}?fields=notes(id,action,comments,dateAdded,commentingPerson(id,firstName,lastName))`;

    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            restUrl: auth.restUrl,
            BhRestToken: auth.BhRestToken,
            endpoint,
            method: 'GET'
        })
    });

    if (!res.ok) {
        let errText = await res.text();
        throw new Error(`Failed to fetch Notes: ${res.status} - ${errText}`);
    }
    const json = await res.json();
    return json.data?.notes?.data || [];
};

let cachedCorpFields: string | null = null;
let cachedContactFields: string | null = null;

export const fetchBullhornClientCorporationsRaw = async (auth: BullhornAuthData, query: string = "isDeleted:0", fetchAll: boolean = false, onProgress?:(msg:string)=>void) => {
    const apiUrl = getBullhornProxyBaseUrl();
    
    if (!cachedCorpFields) {
        try {
            const metaRes = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ restUrl: auth.restUrl, BhRestToken: auth.BhRestToken, endpoint: 'meta/ClientCorporation?fields=*&meta=basic', method: 'GET' })
            });
            if (metaRes.ok) {
                const metaJson = await metaRes.json();
                if (metaJson.fields) {
                    const safeFields = metaJson.fields
                        .filter((f: any) => !f.hidden && (f.type === 'SCALAR' || f.name === 'id') && f.name !== 'customTextBlock1')
                        .map((f: any) => f.name);
                    if (!safeFields.includes('id')) safeFields.push('id');
                    cachedCorpFields = safeFields.join(',');
                }
            }
        } catch (e) { console.warn("Failed caching ClientCorp meta."); }
    }
    
    const fallbackFields = 'id,name,companyURL,phone,billingAddress,status,dateAdded,dateLastModified';
    const fields = cachedCorpFields || fallbackFields;
    
    let allData: any[] = [];
    let start = 0;
    const count = fetchAll ? 200 : 50;

    // Convert lucene syntax to SQL syntax if needed
    const whereClause = query === "isDeleted:0" ? "id>0" : encodeURIComponent(query);

    while (true) {
        const endpoint = `query/ClientCorporation?where=${whereClause}&fields=${fields}&orderBy=-dateAdded&count=${count}&start=${start}`;
        const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ restUrl: auth.restUrl, BhRestToken: auth.BhRestToken, endpoint, method: 'GET' })
        });
        if (!res.ok) {
            let errText = await res.text();
            throw new Error(`Failed to fetch ClientCorporations: ${res.status} - ${errText}`);
        }
        const json = await res.json();
        if (!json.data || json.data.length === 0) break;
        allData = allData.concat(json.data);
        if (onProgress) onProgress(`Bedrijven inladen... (${allData.length} records)`);
        if (!fetchAll || allData.length >= 15000) break;
        start += count;
        if (fetchAll) await delay(300);
    }
    return { data: allData, total: allData.length };
};

export const fetchBullhornClientContactsRaw = async (auth: BullhornAuthData, query: string = "isDeleted:0", fetchAll: boolean = false, onProgress?:(msg:string)=>void) => {
    const apiUrl = getBullhornProxyBaseUrl();
    
    if (!cachedContactFields) {
        try {
            const metaRes = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ restUrl: auth.restUrl, BhRestToken: auth.BhRestToken, endpoint: 'meta/ClientContact?fields=*&meta=basic', method: 'GET' })
            });
            if (metaRes.ok) {
                const metaJson = await metaRes.json();
                if (metaJson.fields) {
                    const safeFields = metaJson.fields
                        .filter((f: any) => !f.hidden && ((f.type === 'SCALAR' || f.name === 'id') && f.name !== 'clientCorporation'))
                        .map((f: any) => f.name);
                    if (!safeFields.includes('id')) safeFields.push('id');
                    cachedContactFields = safeFields.join(',');
                }
            }
        } catch (e) { console.warn("Failed caching ClientContact meta."); }
    }
    
    // Explicitly safe fallback + dynamic fields
    const fallbackFields = 'id,firstName,lastName,email,phone,mobile,status,clientCorporation,dateAdded,dateLastModified';
    const fields = cachedContactFields ? `${cachedContactFields},clientCorporation(id,name)` : fallbackFields;
    
    let allData: any[] = [];
    let start = 0;
    const count = fetchAll ? 200 : 50;

    // Convert lucene syntax to SQL syntax if needed
    const whereClause = query === "isDeleted:0" ? "id>0" : encodeURIComponent(query);

    while (true) {
        const endpoint = `query/ClientContact?where=${whereClause}&fields=${fields}&orderBy=-dateAdded&count=${count}&start=${start}`;
        const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ restUrl: auth.restUrl, BhRestToken: auth.BhRestToken, endpoint, method: 'GET' })
        });
        if (!res.ok) {
            let errText = await res.text();
            throw new Error(`Failed to fetch ClientContacts: ${res.status} - ${errText}`);
        }
        const json = await res.json();
        if (!json.data || json.data.length === 0) break;
        allData = allData.concat(json.data);
        if (onProgress) onProgress(`Contactpersonen inladen... (${allData.length} records)`);
        if (!fetchAll || allData.length >= 15000) break;
        start += count;
        if (fetchAll) await delay(300);
    }
    return { data: allData, total: allData.length };
};

export const fetchBullhornCandidateFiles = async (auth: BullhornAuthData, bhCandidateId: string | number) => {
    const apiUrl = getBullhornProxyBaseUrl();
    const endpoint = `entity/Candidate/${bhCandidateId}/fileAttachments?fields=id,name,description,dateAdded,contentType`;

    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            restUrl: auth.restUrl,
            BhRestToken: auth.BhRestToken,
            endpoint,
            method: 'GET'
        })
    });

    if (!res.ok) {
        console.warn("Failed to fetch files, candidate might not have any or missing auth.", res.status);
        return [];
    }
    const json = await res.json();
    return json.data || [];
};

export const downloadBullhornCandidateFile = async (auth: BullhornAuthData, bhCandidateId: string | number, fileId: string | number) => {
    const apiUrl = getBullhornProxyBaseUrl();
    const endpoint = `file/Candidate/${bhCandidateId}/${fileId}`;

    const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            restUrl: auth.restUrl,
            BhRestToken: auth.BhRestToken,
            endpoint,
            method: 'GET'
        })
    });

    if (!res.ok) throw new Error(`Failed to download File: ${res.status}`);
    return await res.json();
};


export const fetchBullhornPlacementsRaw = async (auth: BullhornAuthData, query: string = "id>0", fetchAll: boolean = false, onProgress?:(msg:string)=>void) => {
    const apiUrl = getBullhornProxyBaseUrl();
    
    // Placements fields mapping to our local db
    const fields = "id,status,candidate(id,firstName,lastName,email,phone),jobOrder(id,title,startDate,employmentType,address(city),location,clientCorporation(id,name)),dateBegin,dateEnd,employmentType,fee,salary,clientCorporation(id,name),dateAdded,dateLastModified,billingClientContact,fileAttachments[5](id,name,contentType,dateAdded),notes[10](id,action,comments,dateAdded,commentingPerson(id,firstName,lastName))";
    let allData: any[] = [];
    let start = 0;
    const count = fetchAll ? 100 : 50;

    // Placements in Bullhorn can be queried via query/Placement
    while (true) {
        const endpoint = `query/Placement?where=${encodeURIComponent(query)}&fields=${encodeURIComponent(fields)}&orderBy=-dateAdded&count=${count}&start=${start}`;
        const res = await fetch(`${apiUrl}/api/bullhorn/proxy`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ restUrl: auth.restUrl, BhRestToken: auth.BhRestToken, endpoint, method: 'GET' })
        });
        if (!res.ok) {
            let errText = await res.text();
            throw new Error(`Failed to fetch Placements: ${res.status} - ${errText}`);
        }
        const json = await res.json();
        if (!json.data || json.data.length === 0) break;
        allData = allData.concat(json.data);
        if (onProgress) onProgress(`Plaatsingen inladen... (${allData.length} records)`);
        if (!fetchAll || allData.length >= 15000) break;
        start += count;
        if (fetchAll) await delay(300);
    }
    return { data: allData, total: allData.length };
};
